import { Alert, LoadingOverlay } from "@mantine/core";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import useLoginStatus, { LoginStatus } from "@lib/hooks/useLoginStatus";
import SocialLoginButtons from "./SocialLoginButtons";
import Head from "next/head";
import { AlertTriangle } from "react-feather";
import AuthLegalDisclaimer from "./AuthLegalDisclaimer";

function AuthWindow({
  action,
  alternativeActionPreamble,
  alternativeActionText,
  alternativeActionLink,
  children,
  isLoading,
  noSocialLogin,
  error,
}: {
  action: string;
  alternativeActionPreamble: string;
  alternativeActionText: string;
  alternativeActionLink: string;
  children: React.ReactNode;
  isLoading: boolean;
  noSocialLogin?: boolean;
  error?: string;
}) {
  const router = useRouter();
  const loginStatus = useLoginStatus();

  const showOAuthEmailError = router.query.error === "oauth-email";

  useEffect(() => {
    if (loginStatus === LoginStatus.LoggedIn) {
      router.push({
        pathname: (router.query.returnUrl as string) ?? "/account/jobs",
      });
    }
  }, [loginStatus, router]);

  return (
    <section className="py-12 bg-gray-50 sm:py-16 lg:py-20">
      <Head>
        <title>{`${action} | Smarketer Jobs`}</title>
      </Head>

      <LoadingOverlay
        visible={isLoading || loginStatus === LoginStatus.Loading}
      />

      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="relative max-w-md mx-auto lg:max-w-lg">
          <div className="absolute -inset-2">
            <div
              className="w-full h-full mx-auto rounded-3xl opacity-30 blur-lg filter"
              style={{
                background:
                  "linear-gradient(90deg, #44ff9a -0.55%, #44b0ff 22.86%, #8b44ff 48.36%, #ff6644 73.33%, #ebff70 99.34%)",
              }}
            />
          </div>
          <div className="relative overflow-hidden bg-white shadow-xl rounded-xl">
            <div className="px-4 py-6 sm:px-8">
              <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900 font-pj">
                  {action}
                </h1>
                <p className="text-base font-normal text-gray-900 font-pj">
                  {alternativeActionPreamble}{" "}
                  <Link
                    href={alternativeActionLink}
                    className="font-bold rounded hover:underline focus:outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-2"
                  >
                    {alternativeActionText}
                  </Link>
                </p>
              </div>
              {error && (
                <Alert
                  icon={<AlertTriangle size={16} />}
                  title={`${action} fehlgeschlagen`}
                  className="mb-4"
                  color="red"
                >
                  {error}
                </Alert>
              )}
              {showOAuthEmailError && (
                <Alert
                  icon={<AlertTriangle size={16} />}
                  title={"Falsche Anmeldemethode"}
                  className="mb-4"
                  color="red"
                >
                  Diese E-Mail Adresse wird bereits von einem Konto verwendet.
                  Bitte melden Sie sich mit der bei der Registrierung genutzten
                  Methode an.
                </Alert>
              )}

              <div className="mt-6">{children}</div>

              {!noSocialLogin && <SocialLoginButtons />}

              <AuthLegalDisclaimer action={action} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AuthWindow;
